import { Box, chakra, Flex, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { appCallbackUrlForUserAgent, forwardToApp } from '@/utils/appCallbackForward';

/**
 * Rendered at /app/callback — where the club web app sends a finished login,
 * as `?clubId=..&state=..#refreshToken=..`. Some contexts (notably the iOS
 * in-app auth sheet) don't hand a universal link straight to the app, so
 * this page forwards the whole query + fragment on to the app's custom
 * scheme instead.
 *
 * SECURITY: the fragment holds the refresh token. It is read only to build
 * the forwarding URL below (`href`) and is never logged, sent to analytics,
 * or round-tripped through a server. Nothing here does a fetch or a console
 * call, so it's not something a future edit can accidentally add without
 * touching this file's shape.
 */
export default function AppCallback(): React.JSX.Element {
  // Guard so the forward fires once per mount even under StrictMode's
  // double-invoked effects (dev), avoiding a double navigation.
  const fired = React.useRef(false);
  const href = appCallbackUrlForUserAgent(
    navigator.userAgent,
    window.location.search,
    window.location.hash,
  );

  React.useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    forwardToApp(navigator.userAgent, window.location.search, window.location.hash);
  }, []);

  return (
    <Flex minH="100vh" align="center" justify="center" px={6} bg="black" color="white">
      <VStack gap={4} maxW="sm" textAlign="center">
        <Box>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="bold"
            style={{ fontFamily: 'Bebas Neue' }}
            mb={2}
          >
            Returning to Olympiq
          </Text>
          <Text fontSize="md" style={{ fontFamily: 'Lato' }} opacity={0.85}>
            If the app doesn&apos;t open automatically, tap below.
          </Text>
        </Box>
        <chakra.a
          href={href}
          bg="white"
          color="black"
          fontWeight="semibold"
          px={6}
          py={3}
          borderRadius="xl"
          display="inline-block"
          style={{ fontFamily: 'Lato' }}
          _hover={{ opacity: 0.85 }}
        >
          Open the Olympiq app
        </chakra.a>
      </VStack>
    </Flex>
  );
}
